// src/components/ChatPanel/ChatPanel.tsx
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useAudioRecorder } from "../../hooks/useAudioRecorder";
import type { SocketMessageHandler } from "../../hooks/useStreamingSocket";
import type {
    AudioRecorderConfig,
    ChatMessage,
    WebSocketMessage,
} from "../../types";
import { VAD_CONFIG } from "../../utils/constants";
import { generateMessageId } from "../../utils/idGenerator";
import { MessageList } from "../MessageList/MessageList";
import "./ChatPanel.scss";

export type SessionControl = { action: "start" | "end"; nonce: number } | null;

interface ChatPanelProps {
    isConnected: boolean;
    sendMessage: (message: WebSocketMessage) => boolean;
    subscribe: (handler: SocketMessageHandler) => () => void;
    sessionControl?: SessionControl;
    onSessionChange?: (sessionId: string | null) => void;
    onSessionStartingChange?: (isStarting: boolean) => void;
    onMessageUpdate?: (messages: ChatMessage[]) => void;
    onAudioChunk?: (audioBase64: string | null, isLast: boolean) => void;
    onTTSStart?: (sampleRate?: number) => void;
    /** 金小卫画面就绪且可接收 TTS 后再播欢迎语、启用输入 */
    avatarReady?: boolean;
    /** 金小卫正在出声，语音模式应暂停倾听 */
    avatarSpeaking?: boolean;
    /** 数字人启动失败（含并发已满） */
    avatarError?: string | null;
    /** 数字人不可用时，对话照常、音频走浏览器扬声器 */
    browserAudioFallback?: boolean;
}

const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            if (typeof result === "string") {
                const base64 = result.split(",")[1];
                resolve(base64 || "");
            } else {
                reject(new Error("Failed to read blob as base64"));
            }
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });
};

const VAD_SPEECH_THRESHOLD = VAD_CONFIG.SPEECH_THRESHOLD;
const VAD_SILENCE_THRESHOLD = VAD_CONFIG.SILENCE_THRESHOLD;
const VAD_SILENCE_DURATION_MS = VAD_CONFIG.SILENCE_DURATION_MS;
const VAD_MIN_SPEECH_MS = VAD_CONFIG.MIN_SPEECH_MS;
const VAD_PREROLL_MS = VAD_CONFIG.PREROLL_MS;
const VAD_RESUME_GUARD_MS = VAD_CONFIG.RESUME_GUARD_MS;
const VAD_RESUME_KEEP_MS = VAD_CONFIG.RESUME_KEEP_MS ?? 2000;
const VAD_MIN_BYTES = Math.round((16000 * VAD_MIN_SPEECH_MS) / 1000) * 2;

export const ChatPanel: React.FC<ChatPanelProps> = ({
    isConnected,
    sendMessage,
    subscribe,
    sessionControl = null,
    onSessionChange,
    onSessionStartingChange,
    onMessageUpdate,
    onAudioChunk,
    onTTSStart,
    avatarReady = false,
    avatarSpeaking = false,
    avatarError = null,
    browserAudioFallback = false,
}) => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [isSessionActive, setIsSessionActive] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isStartingSession, setIsStartingSession] = useState(false);
    const [textInput, setTextInput] = useState("");
    const [chatMode, setChatMode] = useState<"text" | "voice">("text");
    const [listeningSeconds, setListeningSeconds] = useState(0);
    const [onlineSeconds, setOnlineSeconds] = useState(0);
    const onlineStartRef = useRef<number | null>(null);

    const displaySessionId = sessionId;
    const displayIsSessionActive = isSessionActive;
    const displayIsSessionStarting = isStartingSession;
    const inputUnlocked =
        isSessionActive && (avatarReady || browserAudioFallback);
    const endSessionForIdleRef = useRef<() => void>(() => {});
    const idleCloseGuardRef = useRef(false);
    const isEndingSessionForIdleRef = useRef(false);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const sessionIdRef = useRef<string | null>(null);
    sessionIdRef.current = sessionId;
    const onSessionChangeRef = useRef(onSessionChange);
    onSessionChangeRef.current = onSessionChange;
    const sendMessageRef = useRef(sendMessage);
    sendMessageRef.current = sendMessage;
    const streamingMsgIdRef = useRef<string | null>(null);
    const thinkingMsgIdRef = useRef<string | null>(null);
    const userRecognizingMsgIdRef = useRef<string | null>(null);
    const isProcessingSystemMessageRef = useRef(false);
    const playProactiveMessageRef = useRef<
        (text: string, onDone?: () => void) => void
    >(() => {});
    const hasPlayedWelcomeRef = useRef(false);
    const lastProcessedNonceRef = useRef(0);

    const isVadListeningRef = useRef(false);
    const vadStateRef = useRef<"idle" | "speaking" | "silence_pending">("idle");
    const silenceTimerRef = useRef<number | null>(null);
    const speechStartedAtRef = useRef(0);
    const drainPcmBlobRef = useRef<() => Blob>(() => new Blob());
    const keepLastMsRef = useRef<(ms: number) => void>(() => {});
    const cancelRecordingRef = useRef<() => void>(() => {});
    const isProcessingRef = useRef(isProcessing);
    isProcessingRef.current = isProcessing;
    const chatModeRef = useRef(chatMode);
    chatModeRef.current = chatMode;
    const isSessionActiveRef = useRef(isSessionActive);
    isSessionActiveRef.current = isSessionActive;
    const avatarSpeakingRef = useRef(avatarSpeaking);
    avatarSpeakingRef.current = avatarSpeaking;
    const userEchoAddedRef = useRef(false);
    const volumeBarRef = useRef<HTMLDivElement>(null);
    const listeningPulseRef = useRef<HTMLSpanElement>(null);
    const startingRecordingRef = useRef(false);

    const idlePromptTimerRef = useRef<number | null>(null);
    const idleEndTimerRef = useRef<number | null>(null);
    const idleTimersActiveRef = useRef(false);
    const hasPromptedIdleRef = useRef(false);

    // ---------- 占位气泡管理 ----------
    const showThinkingPlaceholder = useCallback(() => {
        if (thinkingMsgIdRef.current) {
            const oldId = thinkingMsgIdRef.current;
            setMessages((prev) => prev.filter((m) => m.id !== oldId));
        }
        const id = generateMessageId();
        thinkingMsgIdRef.current = id;
        setMessages((prev) => [
            ...prev,
            {
                id,
                type: "assistant",
                content: "",
                timestamp: Date.now(),
                isThinking: true,
                sender: "user",
            },
        ]);
    }, []);

    const hideThinkingPlaceholder = useCallback(() => {
        if (thinkingMsgIdRef.current) {
            const oldId = thinkingMsgIdRef.current;
            thinkingMsgIdRef.current = null;
            setMessages((prev) => prev.filter((m) => m.id !== oldId));
        }
    }, []);

    const showRecognizingPlaceholder = useCallback(() => {
        if (userRecognizingMsgIdRef.current) {
            const oldId = userRecognizingMsgIdRef.current;
            setMessages((prev) => prev.filter((m) => m.id !== oldId));
        }
        const id = generateMessageId();
        userRecognizingMsgIdRef.current = id;
        setMessages((prev) => [
            ...prev,
            {
                id,
                type: "user",
                content: "",
                timestamp: Date.now(),
                isRecognizing: true,
                sender: "user",
            },
        ]);
    }, []);

    const replaceRecognizingPlaceholder = useCallback((content: string) => {
        const id = userRecognizingMsgIdRef.current;
        userRecognizingMsgIdRef.current = null;
        if (id) {
            setMessages((prev) =>
                prev.map((m) =>
                    m.id === id
                        ? {
                              ...m,
                              content,
                              isRecognizing: false,
                              timestamp: Date.now(),
                          }
                        : m,
                ),
            );
        } else {
            setMessages((prev) => [
                ...prev,
                {
                    id: generateMessageId(),
                    type: "user" as const,
                    content,
                    timestamp: Date.now(),
                },
            ]);
        }
    }, []);

    const hideRecognizingPlaceholder = useCallback(() => {
        if (userRecognizingMsgIdRef.current) {
            const oldId = userRecognizingMsgIdRef.current;
            userRecognizingMsgIdRef.current = null;
            setMessages((prev) => prev.filter((m) => m.id !== oldId));
        }
    }, []);

    // ---------- 闲置超时 ----------
    const clearIdleTimers = useCallback(() => {
        console.log("[闲置] clearIdleTimers → 清理所有计时器");
        if (idlePromptTimerRef.current) {
            clearTimeout(idlePromptTimerRef.current);
            idlePromptTimerRef.current = null;
        }
        if (idleEndTimerRef.current) {
            clearTimeout(idleEndTimerRef.current);
            idleEndTimerRef.current = null;
        }
        idleTimersActiveRef.current = false;
        hasPromptedIdleRef.current = false;
    }, []);

    const clearIdleTimersOnly = useCallback(() => {
        if (idlePromptTimerRef.current) {
            clearTimeout(idlePromptTimerRef.current);
            idlePromptTimerRef.current = null;
        }
        if (idleEndTimerRef.current) {
            clearTimeout(idleEndTimerRef.current);
            idleEndTimerRef.current = null;
        }
        idleTimersActiveRef.current = false;
    }, []);

    const fallbackBrowserTTS = useCallback(
        (text: string, onDone?: () => void) => {
            console.warn(
                "fallbackBrowserTTS called but audio playback is disabled",
            );
            if (onDone) {
                window.setTimeout(onDone, 0);
            }
        },
        [],
    );

    const playProactiveMessage = useCallback(
        (text: string, onDone?: () => void) => {
            console.log(
                '[闲置] playProactiveMessage → text="' +
                    (text.length > 20 ? text.substring(0, 20) + "..." : text) +
                    '", guard=' +
                    idleCloseGuardRef.current +
                    ", ending=" +
                    isEndingSessionForIdleRef.current,
            );
            if (
                idleCloseGuardRef.current ||
                isEndingSessionForIdleRef.current
            ) {
                console.log("[闲置] playProactiveMessage → 被守卫拦截，不发送");
                if (onDone) {
                    window.setTimeout(() => onDone(), 0);
                }
                return;
            }
            isProcessingSystemMessageRef.current = true;

            const msgId = generateMessageId();
            setMessages((prev) => [
                ...prev,
                {
                    id: msgId,
                    type: "assistant" as const,
                    content: text,
                    timestamp: Date.now(),
                    sender: "system",
                    isSystemMessage: true,
                },
            ]);

            isVadListeningRef.current = false;

            const currentId = sessionIdRef.current;
            if (currentId) {
                setIsProcessing(true);
                const sent = sendMessageRef.current({
                    type: "text_input",
                    sessionId: currentId,
                    data: { text, isProactive: true },
                    timestamp: Date.now(),
                });
                if (!sent) {
                    console.error("Failed to send proactive TTS request");
                    setIsProcessing(false);
                    isProcessingSystemMessageRef.current = false;
                    if (onDone) onDone();
                }
            } else {
                isProcessingSystemMessageRef.current = false;
                fallbackBrowserTTS(text, onDone);
            }
        },
        [fallbackBrowserTTS],
    );

    playProactiveMessageRef.current = playProactiveMessage;

    useEffect(() => {
        if (!isSessionActive || !sessionId || hasPlayedWelcomeRef.current) {
            return;
        }
        if (!avatarReady && !browserAudioFallback) return;
        hasPlayedWelcomeRef.current = true;
        playProactiveMessageRef.current(
            "您好，我是警用无人车金小卫，请问有什么需要我为您服务的？",
        );
    }, [avatarReady, browserAudioFallback, isSessionActive, sessionId]);

    const endSessionForIdle = useCallback(() => {
        console.log(
            "[闲置] endSessionForIdle → 调用, ending=" +
                isEndingSessionForIdleRef.current +
                ", guard=" +
                idleCloseGuardRef.current,
        );
        if (isEndingSessionForIdleRef.current) {
            console.log("[闲置] endSessionForIdle → 已在关闭中，幂等返回");
            return;
        }
        isEndingSessionForIdleRef.current = true;
        idleCloseGuardRef.current = true;
        console.log("[闲置] endSessionForIdle → 守卫已激活, 开始关闭流程");

        clearIdleTimers();
        hasPromptedIdleRef.current = false;

        playProactiveMessage(
            "已经很久没收到您的回复了，我需要休息了，下次可以通过开启对话唤醒我哦，再见。",
        );
        setTimeout(() => {
            const currentId = sessionIdRef.current;
            if (currentId) {
                sendMessageRef.current({
                    type: "end_session",
                    sessionId: currentId,
                    timestamp: Date.now(),
                });
            }
            setIsSessionActive(false);
            setSessionId(null);
            sessionIdRef.current = null;
            setIsProcessing(false);
            isVadListeningRef.current = false;
            setMessages([]);
            onSessionChangeRef.current?.(null);
            isEndingSessionForIdleRef.current = false;
            idleCloseGuardRef.current = false;
            console.log("[闲置] endSessionForIdle → 关闭完成");
        }, 500);
    }, [clearIdleTimers, playProactiveMessage]);

    endSessionForIdleRef.current = endSessionForIdle;

    const startIdleTimers = useCallback(() => {
        if (idleCloseGuardRef.current || isEndingSessionForIdleRef.current) {
            console.log("[闲置] startIdleTimers → 守卫激活，不启动计时器");
            return;
        }
        clearIdleTimersOnly();
        idleTimersActiveRef.current = true;
        console.log(
            "[闲置] startIdleTimers → 启动计时器, hasPromptedIdle=" +
                hasPromptedIdleRef.current,
        );

        if (!hasPromptedIdleRef.current) {
            idlePromptTimerRef.current = window.setTimeout(() => {
                if (
                    idleCloseGuardRef.current ||
                    isEndingSessionForIdleRef.current
                ) {
                    console.log("[闲置] 5分钟定时器触发 → 守卫激活，跳过询问");
                    return;
                }
                console.log("[闲置] 5分钟定时器触发 → 发送主动询问");
                hasPromptedIdleRef.current = true;
                playProactiveMessage(
                    "一直没收到你的回复，请问还需要我继续给你提供帮助吗？",
                );
            }, 300000);
        }

        idleEndTimerRef.current = window.setTimeout(() => {
            if (
                idleCloseGuardRef.current ||
                isEndingSessionForIdleRef.current
            ) {
                console.log("[闲置] 10分钟定时器触发 → 守卫激活，跳过告别");
                return;
            }
            console.log("[闲置] 10分钟定时器触发 → 执行 endSessionForIdle");
            endSessionForIdleRef.current();
        }, 600000);
    }, [clearIdleTimersOnly, playProactiveMessage]);

    const clearIdleTimersRef = useRef<() => void>(() => {});
    clearIdleTimersRef.current = clearIdleTimers;

    const pushVoiceHint = useCallback((text: string) => {
        const id = generateMessageId();
        setMessages((prev) => [
            ...prev,
            {
                id,
                type: "assistant" as const,
                content: text,
                timestamp: Date.now(),
                sender: "system",
                isSystemMessage: true,
            },
        ]);
    }, []);

    // ---------- VAD 触发 ----------
    const sendDrainedVoice = useCallback(async () => {
        const currentId = sessionIdRef.current;
        if (!currentId || avatarSpeakingRef.current) return;

        isVadListeningRef.current = false;
        clearIdleTimersRef.current();

        const blob = drainPcmBlobRef.current();
        // 先校验再出「识别中」占位，避免空包一闪而过
        if (blob.size < VAD_MIN_BYTES) {
            console.warn(
                `[VAD] 语音过短已丢弃: ${blob.size} < ${VAD_MIN_BYTES} bytes`,
            );
            if (
                chatModeRef.current === "voice" &&
                isSessionActiveRef.current &&
                !isProcessingRef.current
            ) {
                isVadListeningRef.current = true;
            }
            return;
        }

        userEchoAddedRef.current = false;
        showRecognizingPlaceholder();
        setIsProcessing(true);

        try {
            const base64 = await blobToBase64(blob);
            const sent = sendMessageRef.current({
                type: "voice_input",
                sessionId: currentId,
                data: { audio: base64, format: "pcm" },
                timestamp: Date.now(),
            });
            if (!sent) {
                console.error("Failed to send voice input");
                setIsProcessing(false);
                hideRecognizingPlaceholder();
                hideThinkingPlaceholder();
                pushVoiceHint("网络未连通，语音未能发送，请检查连接后重试。");
                if (
                    chatModeRef.current === "voice" &&
                    isSessionActiveRef.current
                ) {
                    isVadListeningRef.current = true;
                }
            }
        } catch (error) {
            console.error("Failed to process voice input:", error);
            setIsProcessing(false);
            hideRecognizingPlaceholder();
            hideThinkingPlaceholder();
            pushVoiceHint("语音处理失败，请再说一次。");
            if (
                chatModeRef.current === "voice" &&
                isSessionActiveRef.current
            ) {
                isVadListeningRef.current = true;
            }
        }
    }, [
        showRecognizingPlaceholder,
        hideRecognizingPlaceholder,
        hideThinkingPlaceholder,
        pushVoiceHint,
    ]);

    // ---------- 音量回调 ----------
    const handleVolumeChange = useCallback(
        (volume: number) => {
            if (volumeBarRef.current) {
                const scale = Math.min(1, Math.max(0.05, volume * 3));
                volumeBarRef.current.style.transform = `scaleY(${scale})`;
            }

            if (listeningPulseRef.current) {
                let color = "#ef4444";
                if (volume >= 0.08) {
                    color = "#22c55e";
                } else if (volume >= 0.02) {
                    color = "#4a90d9";
                }
                listeningPulseRef.current.style.backgroundColor = color;
            }

            if (!isVadListeningRef.current) return;

            const state = vadStateRef.current;

            if (state === "idle") {
                if (volume > VAD_SPEECH_THRESHOLD) {
                    keepLastMsRef.current(VAD_PREROLL_MS);
                    speechStartedAtRef.current = Date.now();
                    vadStateRef.current = "speaking";
                    clearIdleTimersRef.current();
                }
            } else if (state === "speaking") {
                if (volume <= VAD_SILENCE_THRESHOLD) {
                    if (
                        Date.now() - speechStartedAtRef.current <
                        VAD_MIN_SPEECH_MS
                    ) {
                        vadStateRef.current = "idle";
                        keepLastMsRef.current(VAD_PREROLL_MS);
                        return;
                    }
                    vadStateRef.current = "silence_pending";
                    if (silenceTimerRef.current) {
                        clearTimeout(silenceTimerRef.current);
                    }
                    silenceTimerRef.current = window.setTimeout(() => {
                        vadStateRef.current = "idle";
                        silenceTimerRef.current = null;
                        void sendDrainedVoice();
                    }, VAD_SILENCE_DURATION_MS);
                }
            } else if (state === "silence_pending") {
                if (volume > VAD_SPEECH_THRESHOLD) {
                    vadStateRef.current = "speaking";
                    if (silenceTimerRef.current) {
                        clearTimeout(silenceTimerRef.current);
                        silenceTimerRef.current = null;
                    }
                }
            }
        },
        [sendDrainedVoice],
    );

    const recorderConfig = useMemo<AudioRecorderConfig>(
        () => ({ onVolumeChange: handleVolumeChange }),
        [handleVolumeChange],
    );

    const {
        isRecording,
        startRecording,
        cancelRecording,
        drainPcmBlob,
        keepLastMs,
        error: recorderError,
    } = useAudioRecorder(recorderConfig);

    drainPcmBlobRef.current = drainPcmBlob;
    keepLastMsRef.current = keepLastMs;
    cancelRecordingRef.current = cancelRecording;

    // ---------- 效果 ----------
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    useEffect(() => {
        onMessageUpdate?.(messages);
    }, [messages, onMessageUpdate]);

    useEffect(() => {
        onSessionStartingChange?.(isStartingSession);
    }, [isStartingSession, onSessionStartingChange]);

    useEffect(() => {
        if (!isConnected) {
            if (isSessionActive) {
                setIsSessionActive(false);
                setSessionId(null);
                sessionIdRef.current = null;
                onSessionChangeRef.current?.(null);
            }
            setIsStartingSession(false);
            setIsProcessing(false);
            isVadListeningRef.current = false;
            clearIdleTimers();
        }
    }, [isConnected, isSessionActive, clearIdleTimers]);

    // ---------- 会话控制 ----------
    useEffect(() => {
        if (!sessionControl) return;
        console.log(
            "[闲置] sessionControl effect → action=" +
                sessionControl.action +
                ", nonce=" +
                sessionControl.nonce +
                ", lastNonce=" +
                lastProcessedNonceRef.current +
                ", isSessionActive=" +
                isSessionActive +
                ", isStarting=" +
                isStartingSession,
        );
        if (sessionControl.nonce === lastProcessedNonceRef.current) {
            console.log(
                "[闲置] sessionControl effect → nonce=" +
                    sessionControl.nonce +
                    " 已处理过，跳过",
            );
            return;
        }
        lastProcessedNonceRef.current = sessionControl.nonce;

        if (sessionControl.action === "start") {
            if (!isConnected || isSessionActive || isStartingSession) {
                console.log(
                    "[闲置] sessionControl effect → start 被守卫拦截 (connected=" +
                        isConnected +
                        ", active=" +
                        isSessionActive +
                        ", starting=" +
                        isStartingSession +
                        ")",
                );
                return;
            }
            console.log(
                "[闲置] sessionControl effect → 发送 start_session, 重置守卫",
            );
            idleCloseGuardRef.current = false;
            isEndingSessionForIdleRef.current = false;
            setIsStartingSession(true);
            const sent = sendMessage({
                type: "start_session",
                timestamp: Date.now(),
            });
            if (!sent) {
                setIsStartingSession(false);
                console.error(
                    "[闲置] sessionControl effect → start_session 发送失败",
                );
            }
        } else if (sessionControl.action === "end") {
            if (!isSessionActive) {
                console.log(
                    "[闲置] sessionControl effect → end: 会话未激活，跳过",
                );
                return;
            }
            console.log("[闲置] sessionControl effect → 用户手动关闭会话");
            idleCloseGuardRef.current = false;
            isEndingSessionForIdleRef.current = false;
            const currentId = sessionIdRef.current;
            if (currentId) {
                sendMessage({
                    type: "end_session",
                    sessionId: currentId,
                    timestamp: Date.now(),
                });
            }
            setIsSessionActive(false);
            setSessionId(null);
            sessionIdRef.current = null;
            setMessages([]);
            setIsProcessing(false);
            isVadListeningRef.current = false;
            clearIdleTimers();
            hideRecognizingPlaceholder();
            hideThinkingPlaceholder();
            onSessionChangeRef.current?.(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionControl, isConnected]);

    // ---------- 消息处理 ----------
    useEffect(() => {
        const handleMessage = (msg: WebSocketMessage) => {
            console.log("Received message:", msg.type, msg);

            if (
                idleCloseGuardRef.current ||
                isEndingSessionForIdleRef.current
            ) {
                if (
                    msg.type !== "connected" &&
                    msg.type !== "pong" &&
                    msg.type !== "end_session"
                ) {
                    console.log(
                        "[闲置] handleMessage → 守卫拦截消息: " + msg.type,
                    );
                    if (
                        msg.type === "processing_start" ||
                        msg.type === "processing_end"
                    ) {
                        setIsProcessing(false);
                        isProcessingSystemMessageRef.current = false;
                    }
                    return;
                }
            }

            switch (msg.type) {
                case "connected":
                    console.log("WebSocket connected:", msg.message);
                    break;

                case "pong":
                    break;

                case "start_session":
                    if (
                        idleCloseGuardRef.current ||
                        isEndingSessionForIdleRef.current
                    ) {
                        console.log(
                            "[闲置] handleMessage → start_session 被守卫拦截（防止自动重启）",
                        );
                        break;
                    }
                    console.log(
                        "[闲置] handleMessage → start_session 收到, sessionId=" +
                            msg.sessionId +
                            ", hasPlayedWelcome=" +
                            hasPlayedWelcomeRef.current,
                    );
                    // 新服务端会话历史为空：清掉旧气泡，避免「界面像连续对话、模型无上下文」
                    setMessages([]);
                    hasPlayedWelcomeRef.current = false;
                    hasPromptedIdleRef.current = false;
                    isEndingSessionForIdleRef.current = false;
                    setSessionId(msg.sessionId || null);
                    sessionIdRef.current = msg.sessionId || null;
                    setIsSessionActive(true);
                    setIsStartingSession(false);
                    setIsProcessing(false);
                    streamingMsgIdRef.current = null;
                    thinkingMsgIdRef.current = null;
                    onSessionChangeRef.current?.(msg.sessionId || null);
                    break;

                case "end_session":
                    console.log(
                        "[闲置] handleMessage → end_session 收到, guard=" +
                            idleCloseGuardRef.current +
                            ", ending=" +
                            isEndingSessionForIdleRef.current,
                    );
                    if (!idleCloseGuardRef.current) {
                        isEndingSessionForIdleRef.current = false;
                    }
                    setIsSessionActive(false);
                    setSessionId(null);
                    sessionIdRef.current = null;
                    setIsStartingSession(false);
                    setIsProcessing(false);
                    isVadListeningRef.current = false;
                    isProcessingSystemMessageRef.current = false;
                    if (
                        !idleCloseGuardRef.current &&
                        !isEndingSessionForIdleRef.current
                    ) {
                        hasPlayedWelcomeRef.current = false;
                    }
                    hasPromptedIdleRef.current = false;
                    hideRecognizingPlaceholder();
                    hideThinkingPlaceholder();
                    clearIdleTimers();
                    onSessionChangeRef.current?.(null);
                    break;

                case "recognized":
                case "user": {
                    const content =
                        msg.type === "recognized" ? msg.text : msg.content;
                    if (content && !userEchoAddedRef.current) {
                        userEchoAddedRef.current = true;
                        replaceRecognizingPlaceholder(content);
                        window.setTimeout(() => {
                            showThinkingPlaceholder();
                        }, 80);
                    }
                    break;
                }

                case "processing_start":
                    setIsProcessing(true);
                    break;

                case "processing_end": {
                    console.log("[闲置] handleMessage → processing_end 收到");
                    setIsProcessing(false);
                    isProcessingSystemMessageRef.current = false;
                    break;
                }

                case "llm_chunk": {
                    const chunk = msg.chunk;
                    if (!chunk) break;
                    if (!streamingMsgIdRef.current) {
                        const thinkingId = thinkingMsgIdRef.current;
                        thinkingMsgIdRef.current = null;
                        const newId = thinkingId || generateMessageId();
                        streamingMsgIdRef.current = newId;
                        const newMsg: ChatMessage = {
                            id: newId,
                            type: "assistant",
                            content: chunk,
                            timestamp: msg.timestamp || Date.now(),
                            sender: "user",
                        };
                        if (thinkingId) {
                            setMessages((prev) =>
                                prev.map((m) =>
                                    m.id === thinkingId ? newMsg : m,
                                ),
                            );
                        } else {
                            setMessages((prev) => [...prev, newMsg]);
                        }
                    } else {
                        const msgId = streamingMsgIdRef.current;
                        setMessages((prev) =>
                            prev.map((m) =>
                                m.id === msgId
                                    ? { ...m, content: m.content + chunk }
                                    : m,
                            ),
                        );
                    }
                    break;
                }

                case "llm_complete":
                    if (streamingMsgIdRef.current && msg.text) {
                        const msgId = streamingMsgIdRef.current;
                        setMessages((prev) =>
                            prev.map((m) =>
                                m.id === msgId
                                    ? { ...m, content: msg.text ?? "" }
                                    : m,
                            ),
                        );
                    }
                    break;

                case "assistant": {
                    const isSystemMsg = isProcessingSystemMessageRef.current;
                    if (isSystemMsg) {
                        console.log("[系统消息] 跳过重复的 assistant 消息");
                        isProcessingSystemMessageRef.current = false;
                        streamingMsgIdRef.current = null;
                        thinkingMsgIdRef.current = null;
                        break;
                    }
                    if (msg.content) {
                        const content = msg.content;
                        if (streamingMsgIdRef.current) {
                            const msgId = streamingMsgIdRef.current;
                            setMessages((prev) =>
                                prev.map((m) =>
                                    m.id === msgId ? { ...m, content } : m,
                                ),
                            );
                        } else {
                            const newMessage: ChatMessage = {
                                id: msg.messageId || generateMessageId(),
                                type: "assistant",
                                content,
                                timestamp: msg.timestamp || Date.now(),
                                sender: "user",
                            };
                            setMessages((prev) => [...prev, newMessage]);
                        }
                    }
                    streamingMsgIdRef.current = null;
                    thinkingMsgIdRef.current = null;
                    break;
                }

                case "tts_start":
                    onTTSStart?.(
                        typeof msg.sampleRate === "number"
                            ? msg.sampleRate
                            : undefined,
                    );
                    break;

                case "tts_chunk": {
                    const audio = msg.audio;
                    if (audio) {
                        onAudioChunk?.(audio, false);
                    }
                    break;
                }

                case "tts_complete":
                    // 只结束本轮 PCM 推流；处理态等 processing_end，避免句间误开录音
                    onAudioChunk?.(null, true);
                    break;

                case "error":
                    console.error("Server error:", msg.message);
                    setIsProcessing(false);
                    setIsStartingSession(false);
                    streamingMsgIdRef.current = null;
                    hideRecognizingPlaceholder();
                    hideThinkingPlaceholder();
                    if (
                        msg.code === "asr_empty" ||
                        /未能识别|听不清|识别/.test(String(msg.message || ""))
                    ) {
                        pushVoiceHint("没听清，请靠近麦克风再说一遍。");
                    }
                    break;
            }
        };

        return subscribe(handleMessage);
    }, [
        subscribe,
        replaceRecognizingPlaceholder,
        showThinkingPlaceholder,
        hideRecognizingPlaceholder,
        hideThinkingPlaceholder,
        clearIdleTimers,
        onAudioChunk,
        onTTSStart,
        pushVoiceHint,
    ]);

    // ---------- 录音管理 ----------
    useEffect(() => {
        if (idleCloseGuardRef.current || isEndingSessionForIdleRef.current) {
            if (isRecording) {
                cancelRecordingRef.current();
            }
            if (silenceTimerRef.current) {
                clearTimeout(silenceTimerRef.current);
                silenceTimerRef.current = null;
            }
            vadStateRef.current = "idle";
            isVadListeningRef.current = false;
            clearIdleTimers();
            return;
        }

        const shouldRecord =
            chatMode === "voice" && inputUnlocked && !isRecording;
        const shouldStop =
            (chatMode !== "voice" || !inputUnlocked) && isRecording;

        if (shouldRecord && !startingRecordingRef.current) {
            startingRecordingRef.current = true;
            void startRecording().finally(() => {
                startingRecordingRef.current = false;
            });
        } else if (shouldStop) {
            cancelRecordingRef.current();
        }

        if (chatMode !== "voice" || !inputUnlocked) {
            if (silenceTimerRef.current) {
                clearTimeout(silenceTimerRef.current);
                silenceTimerRef.current = null;
            }
            vadStateRef.current = "idle";
            isVadListeningRef.current = false;
            clearIdleTimers();
        }
    }, [chatMode, inputUnlocked, isRecording, startRecording, clearIdleTimers]);

    // ---------- VAD 启停 ----------
    useEffect(() => {
        if (idleCloseGuardRef.current || isEndingSessionForIdleRef.current) {
            isVadListeningRef.current = false;
            if (silenceTimerRef.current) {
                clearTimeout(silenceTimerRef.current);
                silenceTimerRef.current = null;
            }
            vadStateRef.current = "idle";
            return;
        }

        if (
            chatMode === "voice" &&
            inputUnlocked &&
            !isProcessing &&
            !avatarSpeaking
        ) {
            const resume = window.setTimeout(() => {
                // 保留守卫期内可能已开口的音频，避免「说完一闪加载却没发出」
                keepLastMsRef.current(VAD_RESUME_KEEP_MS);
                isVadListeningRef.current = true;
                vadStateRef.current = "idle";
            }, VAD_RESUME_GUARD_MS);
            return () => window.clearTimeout(resume);
        }

        isVadListeningRef.current = false;
        if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }
        vadStateRef.current = "idle";
        if (avatarSpeaking) {
            drainPcmBlobRef.current();
        }
    }, [chatMode, inputUnlocked, isProcessing, avatarSpeaking]);

    // ---------- 闲置超时 ----------
    useEffect(() => {
        if (idleCloseGuardRef.current || isEndingSessionForIdleRef.current) {
            console.log("[闲置] idleEffect → 守卫激活，清理计时器并返回");
            clearIdleTimers();
            return;
        }

        if (!isSessionActive) {
            console.log("[闲置] idleEffect → 会话未激活，清理计时器");
            clearIdleTimers();
            return;
        }

        if (isProcessing) {
            console.log("[闲置] idleEffect → 处理中，仅清理计时器");
            clearIdleTimersOnly();
            return;
        }

        if (avatarSpeaking) {
            console.log("[闲置] idleEffect → 数字人播报中，暂停闲置计时");
            clearIdleTimersOnly();
            return;
        }

        console.log("[闲置] idleEffect → 进入闲置状态，启动计时器");
        startIdleTimers();
    }, [
        isSessionActive,
        isProcessing,
        avatarSpeaking,
        startIdleTimers,
        clearIdleTimers,
        clearIdleTimersOnly,
    ]);

    // ---------- 清理 ----------
    useEffect(() => {
        return () => {
            if (silenceTimerRef.current) {
                clearTimeout(silenceTimerRef.current);
                silenceTimerRef.current = null;
            }
            if (idlePromptTimerRef.current) {
                clearTimeout(idlePromptTimerRef.current);
                idlePromptTimerRef.current = null;
            }
            if (idleEndTimerRef.current) {
                clearTimeout(idleEndTimerRef.current);
                idleEndTimerRef.current = null;
            }
            isVadListeningRef.current = false;
        };
    }, []);

    // ---------- 模式切换 ----------
    const handleModeSwitch = useCallback(
        (mode: "text" | "voice") => {
            if (!inputUnlocked) return;
            if (mode === chatMode) return;
            if (mode === "voice") {
                setTextInput("");
            }
            setChatMode(mode);
        },
        [chatMode, inputUnlocked],
    );

    // ---------- 文字发送 ----------
    const handleTextSubmit = useCallback(() => {
        const text = textInput.trim();
        if (!text || !inputUnlocked || !sessionId || isProcessing) return;

        streamingMsgIdRef.current = null;

        const userMsgId = generateMessageId();
        const userMsg: ChatMessage = {
            id: userMsgId,
            type: "user",
            content: text,
            timestamp: Date.now(),
            sender: "user",
        };

        if (thinkingMsgIdRef.current) {
            const oldId = thinkingMsgIdRef.current;
            setMessages((prev) => prev.filter((m) => m.id !== oldId));
            thinkingMsgIdRef.current = null;
        }

        const placeholderId = generateMessageId();
        thinkingMsgIdRef.current = placeholderId;

        setMessages((prev) => [
            ...prev,
            userMsg,
            {
                id: placeholderId,
                type: "assistant",
                content: "",
                timestamp: Date.now(),
                isThinking: true,
            },
        ]);

        userEchoAddedRef.current = true;

        const sent = sendMessage({
            type: "text_input",
            sessionId,
            data: { text },
            timestamp: Date.now(),
        });

        if (sent) {
            setTextInput("");
            setIsProcessing(true);
        } else {
            hideThinkingPlaceholder();
        }
    }, [
        textInput,
        inputUnlocked,
        sessionId,
        isProcessing,
        sendMessage,
        hideThinkingPlaceholder,
    ]);

    const handleTextKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleTextSubmit();
            }
        },
        [handleTextSubmit],
    );

    // ---------- 语音倾听状态 ----------
    const isVoiceListening =
        chatMode === "voice" &&
        inputUnlocked &&
        !isProcessing &&
        !avatarSpeaking &&
        isRecording;

    useEffect(() => {
        if (isVoiceListening) {
            setListeningSeconds(0);
            const timer = window.setInterval(() => {
                setListeningSeconds((prev) => prev + 1);
            }, 1000);
            return () => {
                window.clearInterval(timer);
            };
        }
        setListeningSeconds(0);
        return;
    }, [isVoiceListening]);

    useEffect(() => {
        if (displayIsSessionActive) {
            onlineStartRef.current = Date.now();
            setOnlineSeconds(0);
            const timer = window.setInterval(() => {
                if (onlineStartRef.current) {
                    const elapsed = Math.floor(
                        (Date.now() - onlineStartRef.current) / 1000,
                    );
                    setOnlineSeconds(elapsed);
                }
            }, 1000);
            return () => {
                window.clearInterval(timer);
            };
        } else {
            onlineStartRef.current = null;
            setOnlineSeconds(0);
        }
    }, [displayIsSessionActive]);

    const formatListeningDuration = (totalSeconds: number): string => {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    };

    return (
        <div className="chat-panel">
            <div className="chat-header">
                <div className="header-left">
                    <span className="panel-title">💬 智能对话</span>
                    <div className="header-session-controls">
                        {displaySessionId && (
                            <>
                                <span className="header-session-item">
                                    <span className="header-session-icon">
                                        🆔
                                    </span>
                                    <span className="header-session-label">
                                        会话
                                    </span>
                                    <span className="header-session-value">
                                        {displaySessionId.slice(0, 8)}...
                                    </span>
                                </span>
                                <span className="header-session-divider" />
                            </>
                        )}
                        {displayIsSessionActive && (
                            <>
                                <span className="header-session-item">
                                    <span className="header-session-icon">
                                        ⏱️
                                    </span>
                                    <span className="header-session-label">
                                        在线
                                    </span>
                                    <span
                                        className="header-session-value"
                                        style={{
                                            color: "#22c55e",
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {formatListeningDuration(onlineSeconds)}
                                    </span>
                                </span>
                            </>
                        )}
                    </div>
                </div>
                <div
                    className={`mode-switcher ${!inputUnlocked ? "disabled" : ""}`}
                >
                    <button
                        type="button"
                        className={`mode-tab ${chatMode === "text" ? "active" : ""}`}
                        onClick={() => handleModeSwitch("text")}
                        disabled={!inputUnlocked}
                    >
                        <span className="mode-icon">⌨</span>
                        <span className="mode-label-full">文字对话</span>
                        <span className="mode-label-short">文字</span>
                    </button>
                    <button
                        type="button"
                        className={`mode-tab ${chatMode === "voice" ? "active" : ""}`}
                        onClick={() => handleModeSwitch("voice")}
                        disabled={!inputUnlocked}
                    >
                        <span className="mode-icon">🎤</span>
                        <span className="mode-label-full">语音沟通</span>
                        <span className="mode-label-short">语音</span>
                    </button>
                </div>
            </div>

            <div className="chat-messages">
                {messages.length === 0 ? (
                    <div className={`empty-state ${avatarError && !browserAudioFallback ? "is-busy" : ""}`}>
                        <div className="empty-icon">
                            <img src="/avatar.png" alt="金小卫" />
                        </div>
                        {avatarError && !browserAudioFallback ? (
                            <>
                                <h4>数字人暂时无法接入</h4>
                                <p>{avatarError}</p>
                            </>
                        ) : (
                            <>
                                {browserAudioFallback && (
                                    <p className="fallback-hint">
                                        数字人通道已满，语音将通过设备扬声器播放
                                    </p>
                                )}
                                <p>
                                    {inputUnlocked
                                        ? "输入文字或切换语音模式开始对话"
                                        : displayIsSessionStarting || !avatarReady
                                          ? "金小卫加载中，就绪后将自动问候 …"
                                          : "等待会话就绪 …"}
                                </p>
                            </>
                        )}
                    </div>
                ) : (
                    <MessageList messages={messages} />
                )}
                <div ref={messagesEndRef} />
            </div>

            <div
                className={`chat-input-area ${!inputUnlocked ? "is-locked" : ""}`}
            >
                <div className="input-row">
                    <div className="input-right input-full">
                        {chatMode === "text" ? (
                            <div
                                className={`text-input-wrap ${!inputUnlocked ? "disabled" : ""}`}
                            >
                                <textarea
                                    className="text-input"
                                    value={textInput}
                                    onChange={(e) =>
                                        setTextInput(e.target.value)
                                    }
                                    onKeyDown={handleTextKeyDown}
                                    placeholder={
                                        inputUnlocked
                                            ? "输入文字，回车发送…"
                                            : "金小卫加载中…"
                                    }
                                    disabled={!inputUnlocked}
                                    rows={1}
                                />
                                <button
                                    type="button"
                                    className={`send-btn ${isProcessing ? "loading" : ""}`}
                                    onClick={handleTextSubmit}
                                    disabled={
                                        !inputUnlocked ||
                                        isProcessing ||
                                        !textInput.trim()
                                    }
                                    aria-label={
                                        isProcessing ? "处理中" : "发送"
                                    }
                                    title={
                                        isProcessing
                                            ? "金小卫正在处理中..."
                                            : "发送"
                                    }
                                >
                                    {isProcessing ? (
                                        <span className="spinner" />
                                    ) : (
                                        <span className="send-icon">➤</span>
                                    )}
                                </button>
                            </div>
                        ) : (
                            <div
                                className={`voice-listening ${isVoiceListening ? "active" : ""} ${!inputUnlocked ? "disabled" : ""} ${isProcessing || avatarSpeaking ? "processing" : ""}`}
                            >
                                <span className="mic-icon">🎤</span>
                                <div className="volume-bar">
                                    <div
                                        className="volume-fill"
                                        ref={volumeBarRef}
                                    />
                                </div>
                                <span className="listening-text">
                                    {isProcessing
                                        ? "金小卫正在处理中..."
                                        : avatarSpeaking
                                          ? "金小卫正在播报，请稍候…"
                                          : isVoiceListening
                                            ? `金小卫正在倾听（${formatListeningDuration(listeningSeconds)}），说完稍停即发送…`
                                            : inputUnlocked
                                              ? "准备就绪…"
                                              : "金小卫加载中…"}
                                </span>
                                {isVoiceListening && (
                                    <span
                                        ref={listeningPulseRef}
                                        className="listening-pulse"
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="input-hint">
                    {chatMode === "voice"
                        ? recorderError
                            ? `❌ ${recorderError}`
                            : isProcessing
                              ? "金小卫正在处理中..."
                              : avatarSpeaking
                                ? "播报结束后将自动倾听"
                                : inputUnlocked
                                  ? "开口说话，说完稍停即自动发送"
                                  : "金小卫加载中，请稍候…"
                        : isProcessing
                          ? "金小卫正在处理中..."
                          : inputUnlocked
                            ? "文字回车发送"
                            : "金小卫加载中，请稍候…"}
                </div>
            </div>
        </div>
    );
};

export default ChatPanel;
